import { inject, observer } from "mobx-react";
import * as React from "react";
import * as Sentry from "@sentry/react";

import { Alert, Button, ButtonGroup, Col, Row, Modal, Spinner } from "react-bootstrap";

import Auth from "../../common/auth/auth";
import { Utils } from "../../common/utils";
import { EAIClient } from "Services/EAIClient";

import { UserStore } from "../../stores";
import { SimpleCheckbox } from "Components/Common/SimpleCheckbox";

import CONFIG from "Config";
import { LoadingText } from "Components/Common/LoadingText";
import { AuthError, getAuthErrorMessage } from "../../common/auth/base-auth";
import { redirectToNewUiIfNeeded } from "../../common/auth/new-ui-url";

interface ILoginProps {
    userStore?: UserStore
}

enum LoginStates {
    LOADING = 0,
    INIT = 1,
    SSO = 2,
    USERNAME_PASSWORD = 3,
    RESET_PASSWORD = 4,
    LOGOUT = 5,
    SESSION_EXPIRED = 6
}

interface ILoginState {
    loginState: LoginStates
    password: string
    email: string
    shouldShowPrivacyModal: boolean
    ssoConnectionString: string | undefined
    didSelectDoNotShow: boolean | "" | null
    isLoading: boolean
    isFusionAuth: boolean
    /** Empty string means "no error"; the banner is hidden. */
    errorMessage: string
}

@inject("userStore")
@observer
export class Login extends React.Component<ILoginProps, ILoginState> {

    auth: Auth = new Auth();
    client = new EAIClient();
    state = {
        loginState: LoginStates.LOADING,
        password: "",
        email: new URLSearchParams(location.search).get("email") || "",
        shouldShowPrivacyModal: false,
        ssoConnectionString: undefined,
        didSelectDoNotShow: Utils.shouldSkipPrivacyMessage(),
        isLoading: false,
        isFusionAuth: false,
        errorMessage: "",
    };

    private EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;

    credentialsValid() {
        const { password } = this.state;
        return this.isEmailValid() && password.length > 0;
    }

    isEmailValid = () => {
        const matches = this.state.email.match(this.EMAIL_REGEX) || [];
        return matches.length > 0;
    };

    forgot = () => {
        Utils.setViewName("ForgotPassword", "Forgot Password");
        this.clearError();
        this.transition(LoginStates.RESET_PASSWORD);
    };

    cancelForgot = () => {
        Utils.setViewName("Login", "Login");
        this.setState({ password: "", email: "" });
        this.clearError();
        this.clearMessage();
        this.transition(LoginStates.USERNAME_PASSWORD);
    };

    displayError = (err: AuthError) => {
        this.setState({ errorMessage: getAuthErrorMessage(err) });
    };

    clearError = () => {
        if (this.props.userStore) {
            this.props.userStore.resetPasswordResetMessages();
        }

        this.setState({ errorMessage: "" });
    };

    clearMessage = () => {
        if (this.props.userStore) {
            this.props.userStore.resetPasswordResetMessages();
        }

        const message = document.getElementById("message");
        if (message) {
            message.innerHTML = "";
            message.style.display = "none";
        }
    };

    hidePrivacyModal = () => {
        this.setState({shouldShowPrivacyModal: false});
    };

    handleAcceptPrivacyModal = () => {
        if(this.state.didSelectDoNotShow){
            Utils.setSkipPrivacyMessage();
        }
        this.onLogin();
        this.hidePrivacyModal();
    };

    showPrivacyModal = () => {
        if (!Utils.shouldSkipPrivacyMessage()){
            this.setState({ shouldShowPrivacyModal: true });
        } else {
            this.onLogin();
        }
    };

    resetPassword = () => {
        if (this.props.userStore) {
            this.props.userStore.resetPassword(this.state.email);
        } else {
            this.auth.resetPassword(this.state.email, this.state.isFusionAuth, this.displayError, () => {});
        }
    };

    handleEmailChange = (event: any) => {
        this.setState({ email: event.target.value });
    };

    handlePasswordChange = (event: any) => {
        this.setState({ password: event.target.value });
    };

    handleSubmit = (ev) => {
        ev.preventDefault();
        this.showPrivacyModal();
    };

    handleResetSubmit = (ev) => {
        ev.preventDefault();
        this.resetPassword();
    };

    handleDoNotShowToggle = () => {
        this.setState({didSelectDoNotShow: !this.state.didSelectDoNotShow});
    };

    onLogin = () => {
        this.clearMessage();
        this.clearError();
        this.auth.login(this.state.email, this.state.password, this.state.isFusionAuth, this.displayError);
    };

    onResetEmail = (event: any) => {
        this.setState({ email: event.currentTarget.value } );
    };

    alreadyAuthenticated = () => {
        const { isAuthenticated } = this.auth;
        return isAuthenticated(this.state.isFusionAuth);
    };

    async componentDidMount() {
        Utils.setViewName("LoginView", "Login");

        const searchParams = new URLSearchParams(location.search);

        const state = searchParams.get("state") || null;
        if (state === "logout") {
            this.setState( { loginState: LoginStates.LOGOUT });
            return;
        }
        if (state === "session_expired") {
            this.setState( { loginState: LoginStates.SESSION_EXPIRED });
            return;
        }

        const redirect = searchParams.get("redirect") || null;
        if (redirect) {
            localStorage.setItem("post_login_redirect", redirect);
        }

        // Consume any error stashed by the auth callback. This used to happen
        // inside renderInit, where #error-message did not yet exist on the first
        // render — so the flag was cleared without ever being shown. EAI-5638.
        const authError = localStorage.getItem("authentication_error");
        if (authError) {
            localStorage.removeItem("authentication_error");
            this.setState({ errorMessage: `Login Error: ${authError}` });
        }

        if ((window.location.hostname !== CONFIG.domain || window.location.hostname === "localhost") && !authError) {
            let method;
            try {
                method = await this.auth.getAuthMethod(window.location.hostname);
            } catch (e) {
                // A rejected lookup used to leave this screen on "Loading login
                // page..." forever. The lookup now also decides which UI a
                // school belongs on (EAI-5651), so it must not be the thing that
                // locks users out: fall back to the credential form, which is
                // the one path that works without knowing the answer.
                Sentry.captureException(e);
                this.setState({ loginState: LoginStates.USERNAME_PASSWORD });
                return;
            }

            // New UI Migration Switch (EAI-5651). Only handed off when nothing
            // needs explaining first: an error raised on this screen is about
            // the user's own sign-in attempt, and silently bouncing them would
            // drop it — hence the `!authError` guard above, which the next load
            // no longer trips since the flag is consumed there.
            if (redirectToNewUiIfNeeded(method, {
                email: this.state.email,
                redirect: redirect || localStorage.getItem("post_login_redirect"),
            })) {
                return;
            }

            this.setState({ isFusionAuth: method.is_fusionauth });
            if (method.is_sso) {
                this.setState({ ssoConnectionString: method.connection_string, loginState: LoginStates.SSO});
            } else {
                this.setState({ loginState: LoginStates.USERNAME_PASSWORD});
            }
        } else {
            this.setState( { loginState: LoginStates.INIT} );
        }
    }

    transition = (to: LoginStates) => {
        this.setState({ loginState: to });
    };

    handleNext = async (ev: any) => {
        if (!this.state.email) {
            this.transition(LoginStates.INIT);
        }
        ev.preventDefault();
        this.setState( {isLoading: true} );
        try {
            const url = await this.auth.getUniversityUrl(this.state.email);
            this.setState( {isLoading: false} );
            const params = {};
            params["email"] = this.state.email;
            const redirect = localStorage.getItem("post_login_redirect");
            if (redirect) {
                params["redirect"] = redirect;
            }
            const queryParams = this.client.generateQueryParams(params);
            window.location.href = `${url}/login${queryParams}`;
        } catch (e) {
            if (e && e.response && e.response.status === 404) {
                return this.setState({
                    isLoading: false,
                    loginState: LoginStates.USERNAME_PASSWORD
                });
            }
        }
    };

    handleBack = (ev: any) => {
        ev.preventDefault();
        this.transition(LoginStates.INIT);
    };

    handleSSOLogin = () => {
        return this.auth.authorizeSSOConnection(this.state.ssoConnectionString!, this.state.isFusionAuth);
    };

    handlePasswordLogin = (ev: any) => {
        ev.preventDefault();
        this.showPrivacyModal();
    };

    renderSSOLogin = () => {
        this.handleSSOLogin();
        return (
            <LoadingText text="Redirecting to SSO Login" />
        );
    };

    renderSSOError = () => {
        return (
            <div className="center">An SSO Congiguration Error has occured. Please contact support@enflux.com.</div>
        );
    };

    renderUsernamePassword = () => {
        return (
            <form id="login-form" onSubmit={this.handlePasswordLogin}>
                <input
                    type="email"
                    id="username"
                    placeholder="Email"
                    value={this.state.email}
                    required={true}
                    onChange={this.handleEmailChange}
                />
                <input
                    type="password"
                    id="password"
                    placeholder="Password"
                    value={this.state.password}
                    required={true}
                    onChange={this.handlePasswordChange}
                    autoFocus
                />
                <ButtonGroup vertical>
                    <Button variant="primary" type="submit" id="btn-login"
                            disabled={!this.credentialsValid()}>
                        Sign In
                    </Button>
                    <Button variant="secondary" id="btn-back" onClick={this.handleBack}>
                        Back
                    </Button>
                </ButtonGroup>
                <div className="center">
                    <Button id="btn-forgot" className="forgot" onClick={this.forgot}>
                        Forgot Password?
                    </Button>
                </div>
            </form>
        );
    };

    renderLoading = () => {
        return (
            <div className="center">Loading login page...</div>
        );
    };

    renderInit = () => {
        return (
            <form id="init-form" onSubmit={this.handleSubmit}>
                <input
                    type="email"
                    id="username"
                    placeholder="Email"
                    value={this.state.email}
                    required={true}
                    onChange={this.handleEmailChange}
                />
                <ButtonGroup vertical>
                    <Button variant="primary" type="submit" id="btn-login" disabled={!this.isEmailValid()}
                            onClick={this.handleNext}>
                        { this.state.isLoading
                            ? <Spinner size={ "sm" } animation={"border"} variant={"success"}/>
                            : "Next"
                        }
                    </Button>
                </ButtonGroup>
            </form>
        );
    };

    renderResetPassword = () => {
        return (
            <form id="reset-form" onSubmit={this.handleResetSubmit}>
                <div id="reset-message">
                    Enter the email associated with your account, and we&apos;ll send you
                    instructions for recovering your password.
                </div>
                <input
                    type="email"
                    id="email"
                    placeholder="Email"
                    required={true}
                    onChange={this.onResetEmail}
                    value={this.state.email} />
                <Button className="reset-button" variant="secondary" id="btn-cancel-reset" onClick={this.cancelForgot}>
                    cancel
                </Button>
                <Button className="reset-button" variant="primary" id="btn-reset" type="submit" disabled={!this.isEmailValid}>
                    reset password
                </Button>
                {
                    this.props.userStore && this.props.userStore.passwordResetMessage ?
                        <div id="message" style={{ display: "block" }}>{this.props.userStore.passwordResetMessage}</div>
                        : null
                }
                {
                    this.props.userStore && this.props.userStore.passwordErrorMessage ?
                        <div id="password-error-message" style={{ display: "block" }}>{this.props.userStore.passwordErrorMessage}</div>
                        : null
                }
            </form>
        );
    };

    renderLogout = () => {
        return (
            <>
                <div id="logout-message" className="center">
                    You have successfully logged out.
                </div>
                <div className="center margin-top">
                    <Button id="btn-login-again" onClick={() => window.location.href = "/login"}>
                        Log in Again?
                    </Button>
                </div>
            </>
        );
    };

    renderSessionExpired = () => {
        return (
            <>
                <div id="session-expired-message" className="center">
                    Your session expired due to repeated authentication failures. Please sign in again.
                </div>
                <div className="center margin-top">
                    <Button id="btn-session-expired-sign-in" onClick={() => window.location.href = "/login"}>
                        Sign in
                    </Button>
                </div>
            </>
        );
    };

    renderForm = () => {
        switch(this.state.loginState) {
            case LoginStates.SSO:
                if (!this.state.ssoConnectionString) {
                    return this.renderSSOError();
                } else {
                    return this.renderSSOLogin();
                }
            case LoginStates.USERNAME_PASSWORD:
                return this.renderUsernamePassword();
            case LoginStates.RESET_PASSWORD:
                return this.renderResetPassword();
            case LoginStates.LOGOUT:
                return this.renderLogout();
            case LoginStates.SESSION_EXPIRED:
                return this.renderSessionExpired();
            case LoginStates.INIT:
                return this.renderInit();
            case LoginStates.LOADING:
            default:
                return this.renderLoading();

        }
    };

    renderAlreadyLoggedIn = () => {
        const currentUser = Utils.getCurrentUser();
        return (
            <div className="col-sm-12col col-md-12" style={{ padding: "16px 12px 12px 16px" }}>
                <Alert variant={"warning"} >
                    {`Already logged in as ${currentUser ? currentUser.email : "unknown"}`}
                </Alert>
            </div>
        );
    };

    render() {
        if (this.alreadyAuthenticated()) {
            return this.renderAlreadyLoggedIn();
        }

        return (
            <React.Fragment>
                <Row className="login">
                    <Col md="6" className="left text-center">
                        <div className="logo"/>
                    </Col>
                    <Col md="6" className="right">
                        {/* JSX children, not innerHTML: message text can originate
                            from the API or the auth callback and must not be parsed
                            as markup. EAI-5638. */}
                        <div
                            id="error-message"
                            className="center error-text"
                            role="alert"
                            style={this.state.errorMessage ? { display: "block" } : undefined}>
                            {this.state.errorMessage}
                        </div>
                        {this.renderForm()}
                        <div className="privacy-policy">
                            By logging into Enflux, you agree to our <a target="_blank" rel="noopener noreferrer" href="https://enflux.com/legal/">Terms of
                            Use</a> and <a target="_blank" rel="noopener noreferrer" href="https://enflux.com/privacy/">Privacy Policy</a>.
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <div id="copy-div" className="copyright">© 2017 - {Utils.getCurrentYear()} Enflux. All Rights Reserved</div>
                    </Col>
                </Row>
                <Modal
                    show={this.state.shouldShowPrivacyModal}
                    contentClassName="modal-container confirmation">
                    <div className="alert-icon"><i className="icon icon-exclamation-circle"/></div>
                    <div className="content">
                        You are about to log into a secure system. Taking screenshots, printing or sharing this content
                        in any way invalidates security measures.
                        <br/><br/>
                        <SimpleCheckbox id="silenceWarningCheckbox" isChecked={this.state.didSelectDoNotShow} onChange={this.handleDoNotShowToggle} label="Don't show me this again"/>
                    </div>
                    <div className="alert-action">
                        <Button variant="secondary" size="lg" onClick={this.hidePrivacyModal}
                                style={{marginRight: 10}}>
                            Cancel
                        </Button>
                        <Button variant="primary" size="lg" onClick={this.handleAcceptPrivacyModal}
                                id="okToLoginButton">
                            OK
                        </Button>
                    </div>
                </Modal>
            </React.Fragment>
        );
    }
}
